// Client-side chunking for already-loaded tables. Promoted verbatim from
// admin's `lib/use-paged-rows.ts` (the pattern is proven, zero admin-specific
// coupling) for reuse by a future BI table chart. Every fully-in-memory list
// arrives fully loaded (client-side filter/sort is the house pattern), but
// painting thousands of <tr>s at once makes the tab crawl; this shows the
// first N and grows by N per click. Purely presentational: it never refetches.
import { useEffect, useRef } from "react";

import { useRestoredState } from "./view-state";

export const TABLE_PAGE_SIZE = 100;

export interface PagedRows<T> {
  /** The rows to render right now (first `visible` of the input). */
  shown: readonly T[];
  hasMore: boolean;
  remaining: number;
  total: number;
  showMore: () => void;
}

export interface PagedRowsOptions {
  /** Rows per page, and per "Show more". */
  pageSize?: number;
  /** Opt the grown window into surviving a history BACK hop, under a key unique
   *  across the app (`view-state.ts`). This is what makes the router's scroll
   *  restoration mean anything on a long table: restoring an offset 2,000px down
   *  a list that came back only `pageSize` rows tall has nowhere to land. */
  restore?: string;
}

/** The input state the reset rule reads, as one comparable string. Both halves
 *  matter: a changed row COUNT is a filter edit or a refetch, and a changed page
 *  size is a different window entirely. */
export function rowsSignature(total: number, pageSize: number): string {
  return `${total}:${pageSize}`;
}

/**
 * What the window should do about the rows it is looking at. `seen` is the
 * signature already accepted, or null before any has been.
 *
 * `"adopt"` — take the new signature, KEEP the window — covers two cases:
 *
 *  * the RESTORE case. A window restored by a back hop is in place before the
 *    rows are (the page remounts and its query resolves a tick later, or resolves
 *    with a slightly different count than it had), and treating that first
 *    arrival as a filter edit would throw away the very window just restored. A
 *    fresh mount is unaffected either way: its window is already `pageSize`, so
 *    the reset it skips would have been a no-op.
 *  * the APPEND case: the SAME list, extended. On every table that is both
 *    server-paged and locally windowed (`ShowMoreRow` drains the window first and
 *    then fetches the next cursor), a "load more" lands as a row count that GREW,
 *    and resetting there snapped the list back to the first `pageSize` rows the
 *    instant the page the reader asked for arrived. The rows they had just read
 *    vanished under them, and the sentinel then re-grew the window a page at a
 *    time, which is the "infinite scroll that jumps back to the top" the talent
 *    preferences queue was reported for.
 *
 * Growth alone does NOT prove an append, which is why `appended` is passed in
 * rather than inferred from the count: broadening a filter, or switching to a
 * bigger view of the same table, also grows the list, and keeping a window grown
 * over one list onto a DIFFERENT one paints thousands of rows at once — the exact
 * cost the window exists to avoid. The caller answers the question it can actually
 * answer: are the rows I had still the front of the rows I have?
 *
 * The residual, stated rather than hidden: the caller's signal is the identity of
 * the FIRST row, so a list sorted ASCENDING whose newly fetched page contains a row
 * that sorts before the current first reads as a different list and resets. That is
 * the behavior every one of these tables had before this rule existed, so it is a
 * narrowed bug rather than a new one; the default sorts (newest first) append at the
 * tail, which is the case that was actually broken.
 *
 * A count that SHRANK is the filter edit the reset rule exists for, and still
 * resets: a window grown over 5,000 rows must not survive onto the 40 that match.
 */
export function windowResetAction(
  seen: string | null,
  signature: string,
  total: number,
  appended = false,
): "ignore" | "adopt" | "reset" {
  // Nothing has loaded yet. An empty table cannot have had its rows CHANGE, and a
  // restored window has to outlive the empty first render.
  if (seen === null && total === 0) return "ignore";
  if (seen === null) return "adopt";
  if (seen === signature) return "ignore";
  const [seenTotal, seenPageSize] = seen.split(":");
  const [, pageSize] = signature.split(":");
  // A different page size is a different window entirely, whatever the count did.
  if (seenPageSize !== pageSize) return "reset";
  return appended && total > Number(seenTotal) ? "adopt" : "reset";
}

/** Show the first `pageSize` rows and grow by `pageSize` per `showMore`.
 *  Resets when the row count SHRINKS (a filter edit), but not on reference churn
 *  and not on rows being appended, so a sort toggle and a server "load more" both
 *  keep the window the reader grew. */
export function usePagedRows<T>(rows: readonly T[], options: PagedRowsOptions = {}): PagedRows<T> {
  const { pageSize = TABLE_PAGE_SIZE, restore } = options;
  const [visible, setVisible] = useRestoredState(restore, pageSize);

  const seen = useRef<string | null>(null);
  // The FIRST row of the list the window was last accepted against. A server page
  // being appended leaves the earlier pages' row objects untouched (they are not
  // refetched), so an unchanged first row is what separates "the same list, longer"
  // from a different list that merely happens to be bigger. A refetch that replaces
  // every page produces a new object here and resets, which is the safe answer.
  const seenFirst = useRef<unknown>(undefined);
  useEffect(() => {
    const signature = rowsSignature(rows.length, pageSize);
    const first = rows[0];
    const appended = seenFirst.current !== undefined && Object.is(seenFirst.current, first);
    const action = windowResetAction(seen.current, signature, rows.length, appended);
    // Recorded on EVERY run, including the ignored ones. A sort toggle changes which
    // row is first while the count holds, and a first row left stale across one would
    // make the NEXT append look like a different list and reset the window there.
    seenFirst.current = first;
    if (action === "ignore") return;
    seen.current = signature;
    if (action === "reset") setVisible(pageSize);
  }, [rows, pageSize, setVisible]);

  const shown = rows.length > visible ? rows.slice(0, visible) : rows;
  return {
    shown,
    hasMore: rows.length > visible,
    remaining: Math.max(rows.length - visible, 0),
    total: rows.length,
    showMore: () => setVisible((v) => v + pageSize),
  };
}

/** The pure windowing rule behind the hook, kept separate for its unit test. */
export function pagedWindow(
  total: number,
  visible: number,
): { shownCount: number; remaining: number } {
  const shownCount = Math.min(total, Math.max(visible, 0));
  return { shownCount, remaining: Math.max(total - shownCount, 0) };
}
